import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Search, Car } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import TablePagination from "../components/TablePagination";
import { fmtDate } from "../lib/format";

export default function DisposalRecords() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => { api.get("/disposal-records").then((r) => setRecords(r.data)); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = records.filter((r) => {
    const q = search.toLowerCase();
    return !q || `${r.vehicle_reg} ${r.vehicle_model} ${r.kaedah} ${r.sebab} ${r.rujukan}`.toLowerCase().includes(q);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="space-y-6 fade-up" data-testid="disposal-records-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Archive className="h-6 w-6 text-[#10056E]" /> Pelupusan Kenderaan</h1>
        <p className="text-sm text-muted-foreground mt-1">Rekod pelupusan aset mengikut prosedur KEW.PA — {records.length} rekod.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-4 py-3 min-w-40">
          <Archive className="h-4 w-4 text-[#10056E]" />
          <p className="text-2xl font-bold mt-1" data-testid="disposal-total-count">{records.length}</p>
          <p className="text-xs text-muted-foreground">jumlah rekod pelupusan</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-4 py-3 min-w-40">
          <Car className="h-4 w-4 text-slate-500" />
          <p className="text-2xl font-bold mt-1">{new Set(records.map((r) => r.vehicle_id)).size}</p>
          <p className="text-xs text-muted-foreground">kenderaan dilupus</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex justify-end">
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Cari kenderaan, kaedah, rujukan..." value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="disposal-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/70">
                <TableHead>Tarikh Pelupusan</TableHead>
                <TableHead>Kenderaan</TableHead>
                <TableHead>Kaedah</TableHead>
                <TableHead>Sebab</TableHead>
                <TableHead>No. Rujukan</TableHead>
                <TableHead>Catatan</TableHead>
                <TableHead>Direkod Oleh</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Tiada rekod pelupusan.</TableCell></TableRow>
              )}
              {paged.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/kenderaan/${r.vehicle_id}`)} data-testid={`disposal-row-${r.id}`}>
                  <TableCell className="text-sm">{r.tarikh ? fmtDate(r.tarikh) : "-"}</TableCell>
                  <TableCell className="text-sm">
                    <span className="font-semibold">{r.vehicle_reg}</span>
                    <span className="text-xs text-muted-foreground block">{r.vehicle_model} · {r.category}</span>
                  </TableCell>
                  <TableCell><Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">{r.kaedah || "-"}</Badge></TableCell>
                  <TableCell className="text-sm">{r.sebab || "-"}</TableCell>
                  <TableCell className="text-sm font-mono text-xs">{r.rujukan || "-"}</TableCell>
                  <TableCell className="text-sm max-w-52 truncate">{r.catatan || "-"}</TableCell>
                  <TableCell className="text-sm">{r.recorded_by || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />
      </div>
    </div>
  );
}
